import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Button, Grid, Stack, Typography } from '@mui/material';

import { formatDateSlashWithTime, formatDateSlashes } from '~/services/TimeService';

import type { ContractingQueueCase } from '~/types/services/contracting';

interface Props {
  caseItem: ContractingQueueCase;
  caseHref: string;
}

const EMPTY_VALUE = '-';

interface DetailEntry {
  label: string;
  value: string | null;
}

export default function CaseExpandedDetails({ caseItem, caseHref }: Props) {
  const entries: Array<DetailEntry> = [
    { label: 'Case Number', value: caseItem.case_number },
    { label: 'Status', value: caseItem.status },
    { label: 'Priority', value: caseItem.priority },
    { label: 'Case Owner', value: caseItem.owner.name },
    { label: 'Original Case Owner', value: caseItem.original_owner_name },
    { label: 'Opportunity', value: caseItem.opportunity?.name ?? null },
    { label: 'Contact Account Name', value: caseItem.contact_account?.name ?? null },
    { label: 'Customers Email', value: caseItem.customer_email },
    { label: 'Date/Time Opened', value: formatDateSlashWithTime(caseItem.created_at) },
    { label: 'Case Last Modified Date', value: formatDateSlashWithTime(caseItem.last_modified_at) },
    {
      label: 'Booking Check-In Date',
      value: caseItem.booking_check_in_date && formatDateSlashes(caseItem.booking_check_in_date),
    },
    { label: 'Case Origin', value: caseItem.origin },
    { label: 'Case Reason', value: caseItem.reason },
    { label: 'Zendesk Id', value: caseItem.zendesk_id },
  ];

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Subject
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {caseItem.subject || EMPTY_VALUE}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {entries.map((entry) => (
            <Grid key={entry.label} size={{ xs: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {entry.label}
              </Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {entry.value || EMPTY_VALUE}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Short Detail
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            {caseItem.short_detail || EMPTY_VALUE}
          </Typography>
        </Box>
        <Box>
          <Button component={Link} to={caseHref} size="small" variant="outlined" sx={{ textTransform: 'none' }}>
            Open case
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
